import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const NEW_STYLE = "bg-blue-50 text-blue-800 border-blue-200";
const DONE_STYLE = "bg-emerald-50 text-emerald-800 border-emerald-200";

export default function Inbox() {
  const [contacts, setContacts] = useState([]);
  const [pita, setPita] = useState([]);
  const [acct, setAcct] = useState([]);
  const [subs, setSubs] = useState([]);
  const [invites, setInvites] = useState([]);

  const load = () => {
    adminApi.get("/admin/contact-messages").then((r) => setContacts(r.data)).catch(() => {});
    adminApi.get("/admin/pita-requests").then((r) => setPita(r.data)).catch(() => {});
    adminApi.get("/admin/accountability-requests").then((r) => setAcct(r.data)).catch(() => {});
    adminApi.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {});
    adminApi.get("/admin/invitations").then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const mark = async (path, id, status) => {
    try {
      await adminApi.patch(`${path}/${id}`, { status });
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="max-w-6xl">
      <h1 className="font-heading font-bold text-2xl text-foreground mb-6" data-testid="admin-inbox-title">Inbox</h1>
      <Tabs defaultValue="contact">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="contact" data-testid="inbox-tab-contact">Contact ({contacts.filter((c) => c.status === "new").length})</TabsTrigger>
          <TabsTrigger value="pita" data-testid="inbox-tab-pita">PITA requests ({pita.filter((c) => c.status === "new").length})</TabsTrigger>
          <TabsTrigger value="acct" data-testid="inbox-tab-acct">Accountability ({acct.filter((c) => c.status === "new").length})</TabsTrigger>
          <TabsTrigger value="invites" data-testid="inbox-tab-invites">Invitations ({invites.filter((c) => c.status === "pending").length})</TabsTrigger>
          <TabsTrigger value="subs" data-testid="inbox-tab-subscribers">Alert subscribers ({subs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="contact" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>From</TableHead><TableHead>Subject</TableHead><TableHead>Message</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {contacts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-8">No messages</TableCell></TableRow>}
                {contacts.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm font-medium">{c.name}<p className="text-xs text-muted-foreground">{c.email}</p></TableCell>
                    <TableCell className="text-sm">{c.subject}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-sm"><span className="line-clamp-2">{c.message}</span></TableCell>
                    <TableCell><Badge variant="outline" className={c.status === "new" ? NEW_STYLE : DONE_STYLE}>{c.status}</Badge></TableCell>
                    <TableCell>{c.status === "new" && <Button size="sm" variant="outline" onClick={() => mark("/admin/contact-messages", c.id, "handled")} data-testid={`contact-handle-${c.id}`}>Mark handled</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="pita" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>From</TableHead><TableHead>Municipality</TableHead><TableHead>Role</TableHead><TableHead>Message</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {pita.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-sm py-8">No PITA requests</TableCell></TableRow>}
                {pita.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm font-medium">{c.name}<p className="text-xs text-muted-foreground">{c.email}</p></TableCell>
                    <TableCell className="text-sm">{c.municipality}</TableCell>
                    <TableCell><Badge variant="outline">{c.role}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-xs"><span className="line-clamp-2">{c.message || "—"}</span></TableCell>
                    <TableCell><Badge variant="outline" className={c.status === "new" ? NEW_STYLE : DONE_STYLE}>{c.status}</Badge></TableCell>
                    <TableCell>{c.status !== "resolved" && <Button size="sm" variant="outline" onClick={() => mark("/admin/pita-requests", c.id, "resolved")}>Resolve</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="acct" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>From</TableHead><TableHead>Municipality</TableHead><TableHead>Topic</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {acct.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-8">No requests</TableCell></TableRow>}
                {acct.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm font-medium">{c.name}<p className="text-xs text-muted-foreground">{c.email}</p></TableCell>
                    <TableCell className="text-sm">{c.municipality}</TableCell>
                    <TableCell className="text-sm max-w-xs"><span className="line-clamp-2">{c.topic}</span></TableCell>
                    <TableCell><Badge variant="outline" className={c.status === "new" ? NEW_STYLE : DONE_STYLE}>{c.status}</Badge></TableCell>
                    <TableCell>{c.status !== "resolved" && <Button size="sm" variant="outline" onClick={() => mark("/admin/accountability-requests", c.id, "resolved")}>Resolve</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="invites" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Invitee</TableHead><TableHead>Municipality / Office</TableHead><TableHead>Sender</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {invites.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-8">No invitations</TableCell></TableRow>}
                {invites.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm font-medium">{c.invitee_name}<p className="text-xs text-muted-foreground">{c.invitee_email}</p></TableCell>
                    <TableCell className="text-sm">{[c.municipality_name, c.office].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell className="text-sm">{c.sender_name}</TableCell>
                    <TableCell><Badge variant="outline" className={c.status === "pending" ? NEW_STYLE : DONE_STYLE}>{c.status}</Badge></TableCell>
                    <TableCell>{c.status === "pending" && <Button size="sm" variant="outline" onClick={() => mark("/admin/invitations", c.id, "reviewed")}>Mark reviewed</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="subs" className="mt-4">
          <Card><CardContent className="p-0">
            <Table data-testid="subscribers-table">
              <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Municipality / Ward</TableHead><TableHead>Topics</TableHead><TableHead>Since</TableHead></TableRow></TableHeader>
              <TableBody>
                {subs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-8">No subscribers yet</TableCell></TableRow>}
                {subs.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-sm font-medium">{s.email}</TableCell>
                    <TableCell className="text-sm">{s.name || "—"}</TableCell>
                    <TableCell className="text-sm">{[s.municipality, s.ward].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell><div className="flex flex-wrap gap-1">{(s.topics || []).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}</div></TableCell>
                    <TableCell className="font-mono text-xs">{s.created_at?.slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-[11px] text-muted-foreground p-4">
              Alert subscription data is stored completely separately from voter declaration data.
            </p>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
